<template>
  <div
    v-if="localShowPopUp"
    class="voice-popup-overlay"
    @click.self="handleClose"
  >
    <div class="voice-popup-content">
      <!-- 可视化绘制区域 -->
      <div class="no-audio-tip-container">
        <div
          class="recwave"
          :style="{
            background: isCanceling
              ? 'linear-gradient(180deg, #FF7878 0%, #FF6767 100%)'
              : 'linear-gradient(180deg, #F0F3FF 0%, #E1E7FF 100%)',
          }"
        >
          <canvas
            v-if="isStart"
            ref="waveCanvas"
            class="recwave-WaveView"
            width="253"
            height="100"
          ></canvas>
          <div v-else class="line" :style="{ background: lineGradient }"></div>
        </div>
      </div>

      <!-- 取消按钮提示 -->
      <div class="cancel-btn">
        <span v-if="isCanceling">松开取消</span>
        <img
          v-if="isCanceling"
          src="@/assets/ic-voice-close.png"
          alt=""
          class="cancel-icon"
        />
        <img
          v-else
          src="@/assets/ic-voice-close.png"
          alt=""
          class="cancel-icon"
        />
        <span v-if="!isCanceling">松开发送</span>
      </div>

      <!-- 语音按钮 -->
      <div class="voice-btn">
        <img
          v-if="isCanceling"
          src="@/assets/ic-voice-close.png"
          alt=""
          class="voice-icon-img"
           style="width: 37px; height: 37px;"
        />
        <img v-else src="@/assets/ic-voice.png" alt="" class="voice-icon-img" />
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, watch, onMounted, onUnmounted, nextTick, computed } from "vue";
import { blobToBase64 } from "@/utils/audio.js";

const props = defineProps({
  showPopUp: {
    type: Boolean,
    default: false,
  },
  isCanceling: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:showPopUp", "close", "saveVoice"]);

const localShowPopUp = ref(false);
const isStart = ref(false);
const waveCanvas = ref(null);
const audioContext = ref(null);
const analyser = ref(null);
const animationFrameId = ref(null);

// 录音相关
let mediaRecorder = null;
let audioChunks = [];
let recordingStream = null;
let shouldSaveRecording = true; // 标记是否应该保存录音

// 渐变颜色配置（用于 .line 和波形绘制）
const WHITE_WAVE_COLOR = '#FFFFFF'; // 白色波浪线条色（取消时使用）
const wavePositions = [0, 0.18, 0.4, 0.6, 0.82, 1]; // 渐变位置（6个位置点）

// 获取粗线颜色配置
const getThickColors = () => {
  if (props.isCanceling) {
    // 取消时使用纯白色（不需要渐变，直接返回白色）
    return [WHITE_WAVE_COLOR];
  }
  // 使用 walin.html 的渐变色（6个颜色点，左右完全透明）
  return [
    'rgba(136, 128, 255, 0.0)',  // 最左：完全透明
    'rgba(134, 132, 255, 1.0)',  // #8684FF
    'rgba(122, 158, 255, 1.0)',  // #7A9EFF
    'rgba(107, 187, 255, 1.0)',  // #6BBBFF
    'rgba(99, 223, 255, 1.0)',   // #63DFFF
    'rgba(97, 113, 231, 0.0)'    // 最右：完全透明
  ];
};

// 计算属性：生成 .line 的渐变背景色（使用 getThickColors 的颜色配置）
const lineGradient = computed(() => {
  const colors = getThickColors();
  // 如果是取消状态（纯白色），返回白色渐变
  if (props.isCanceling && colors.length === 1) {
    return `linear-gradient(89deg, ${colors[0]} 0%, ${colors[0]} 100%)`;
  }
  // 使用渐变色配置生成 CSS linear-gradient
  const gradientStops = colors.map((color, index) => {
    const position = wavePositions[index] * 100;
    return `${color} ${position}%`;
  }).join(', ');
  return `linear-gradient(89deg, ${gradientStops})`;
});

// 监听 showPopUp 变化
watch(
  () => props.showPopUp,
  (newVal) => {
    localShowPopUp.value = newVal;
    if (newVal) {
      resetAllStates();
      // 立即启动可视化，不等待录音权限
      nextTick(() => {
        // 先立即显示波形（使用模拟数据）
        startImmediateVisualization();
        // 然后在后台异步初始化录音
        initRecording().catch(err => {
          console.error("录音初始化失败（不影响波形显示）:", err);
        });
      });
    } else {
      // 当弹窗关闭时，停止录音
      // 注意：这里不立即停止，而是让父组件的 cancelPress 来控制
      // 如果已经在录音，则停止
      if (isStart.value) {
        stopRecording();
      }
    }
  },
  { immediate: true }
);

// 监听 isCanceling 变化（用于更新UI和标记是否保存）
watch(
  () => props.isCanceling,
  (newVal) => {
    // 更新保存标记
    shouldSaveRecording = !newVal;
  }
);

// 初始化录音
async function initRecording() {
  const hasPermission = await requestMicrophonePermission();
  if (hasPermission) {
    startRecording();
  }
}

// 请求录音权限
async function requestMicrophonePermission() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    // 权限获取成功，先关闭流
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch (err) {
    console.error("录音权限获取失败:", err);
    const isUserNotAllow =
      err.name === "NotAllowedError" || err.name === "PermissionDeniedError";
    if (isUserNotAllow) {
      handleClose();
    }
    return false;
  }
}

// 开始录音
async function startRecording() {
  try {
    // 获取音频流
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    recordingStream = stream;
    audioChunks = [];
    isStart.value = true;

    // 创建 MediaRecorder
    mediaRecorder = new MediaRecorder(stream, {
      mimeType: "audio/webm;codecs=opus",
    });

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunks.push(event.data);
      }
    };

    mediaRecorder.onstop = async () => {
      // 停止所有音频轨道
      if (recordingStream) {
        recordingStream.getTracks().forEach((track) => track.stop());
        recordingStream = null;
      }

      // 处理录音数据（只有在非取消状态下才保存）
      if (audioChunks.length > 0 && shouldSaveRecording) {
        const audioBlob = new Blob(audioChunks, { type: "audio/webm" });
        const base64Audio = await blobToBase64(audioBlob);
        emit("saveVoice", base64Audio);
      }

      audioChunks = [];
      isStart.value = false;
      stopVisualization();
      shouldSaveRecording = true; // 重置标记
    };

    // 开始录音
    mediaRecorder.start();

    // 切换到真实音频可视化（如果之前是模拟数据，现在切换到真实数据）
    nextTick(() => {
      // 停止之前的模拟可视化
      if (animationFrameId.value) {
        cancelAnimationFrame(animationFrameId.value);
        animationFrameId.value = null;
      }
      // 启动真实音频可视化
      initVisualization(stream);
    });
  } catch (error) {
    console.error("录音启动失败:", error);
    isStart.value = false;
    handleClose();
  }
}

// 停止录音
function stopRecording() {
  if (mediaRecorder && mediaRecorder.state !== "inactive") {
    mediaRecorder.stop();
  }

  // 停止所有音频轨道
  if (recordingStream) {
    recordingStream.getTracks().forEach((track) => track.stop());
    recordingStream = null;
  }

  isStart.value = false;
  stopVisualization();
}

// 初始化可视化
function initVisualization(stream) {
  try {
    // 确保 canvas 已经渲染
    if (!waveCanvas.value) {
      console.warn("Canvas 未准备好，延迟初始化可视化");
      setTimeout(() => {
        if (waveCanvas.value && isStart.value) {
          initVisualization(stream);
        }
      }, 100);
      return;
    }

    // 确保canvas的实际尺寸和显示尺寸一致（90%容器宽度，与静态线条一致）
    // 使用 devicePixelRatio 确保高分辨率显示清晰
    const canvas = waveCanvas.value;
    const displayWidth = 253; // 281 * 0.9 ≈ 253，与静态线条长度一致
    const displayHeight = 100;
    // 使用更高的分辨率倍数，确保线条清晰（至少 2 倍，高分辨率屏幕使用实际倍数）
    const pixelRatio = Math.max(2, window.devicePixelRatio || 1);
    
    canvas.width = displayWidth * pixelRatio;
    canvas.height = displayHeight * pixelRatio;
    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;
    
    const ctx = canvas.getContext("2d", { 
      alpha: true,
      desynchronized: false 
    });
    ctx.scale(pixelRatio, pixelRatio);
    // 启用高质量图像平滑，但使用更高的分辨率来保证清晰度
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    audioContext.value = new (window.AudioContext ||
      window.webkitAudioContext)();
    analyser.value = audioContext.value.createAnalyser();
    const source = audioContext.value.createMediaStreamSource(stream);
    source.connect(analyser.value);

    analyser.value.fftSize = 256;
    analyser.value.smoothingTimeConstant = 0.8;
    const bufferLength = analyser.value.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const frequencyDataArray = new Uint8Array(bufferLength); // 频域数据数组

    // 延迟一下确保 canvas 完全准备好
    nextTick(() => {
      drawWaveform(dataArray, frequencyDataArray, bufferLength);
    });
  } catch (error) {
    console.error("可视化初始化失败:", error);
  }
}

// 立即启动可视化（不等待录音权限）
function startImmediateVisualization() {
  if (!waveCanvas.value) {
    setTimeout(() => {
      if (waveCanvas.value) {
        startImmediateVisualization();
      }
    }, 50);
    return;
  }

  const canvas = waveCanvas.value;
  const displayWidth = 253; // 281 * 0.9 ≈ 253，与静态线条长度一致
  const displayHeight = 100;
  // 使用更高的分辨率倍数，确保线条清晰（至少 2 倍，高分辨率屏幕使用实际倍数）
  const pixelRatio = Math.max(2, window.devicePixelRatio || 1);
  
  canvas.width = displayWidth * pixelRatio;
  canvas.height = displayHeight * pixelRatio;
  canvas.style.width = `${displayWidth}px`;
  canvas.style.height = `${displayHeight}px`;
  
  const ctx = canvas.getContext("2d", { 
    alpha: true,
    desynchronized: false 
  });
  ctx.scale(pixelRatio, pixelRatio);
  // 启用高质量图像平滑，但使用更高的分辨率来保证清晰度
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  isStart.value = true;

  // 创建模拟的 dataArray（用于立即显示）
  const bufferLength = 128;
  const dataArray = new Uint8Array(bufferLength);

  // 立即开始绘制（使用模拟数据）
  nextTick(() => {
    drawWaveformImmediate(dataArray, bufferLength);
  });
}

// 立即绘制波形（使用模拟数据，直到真实音频数据准备好）
function drawWaveformImmediate(dataArray, bufferLength) {
  if (!waveCanvas.value) return;

  const canvas = waveCanvas.value;
  const ctx = canvas.getContext("2d", { 
    alpha: true,
    desynchronized: false 
  });
  // 确保渲染质量设置（每次获取 context 时都需要设置）
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  // 使用显示尺寸（ctx 已经 scale 过了，所以使用显示尺寸）
  const width = 253; // 显示宽度
  const height = 100; // 显示高度
  let useRealData = false; // 是否已切到真实数据
  let timeOffset = 0; // 时间偏移，用于动画

  function draw() {
    if (!isStart.value || !localShowPopUp.value) {
      if (animationFrameId.value) {
        cancelAnimationFrame(animationFrameId.value);
        animationFrameId.value = null;
      }
      return;
    }

    animationFrameId.value = requestAnimationFrame(draw);

    // 有真实数据时切到真实绘制
    if (analyser.value && !useRealData) {
      useRealData = true;
      const freqDataArray = new Uint8Array(analyser.value.frequencyBinCount);
      drawWaveform(dataArray, freqDataArray, analyser.value.frequencyBinCount);
      return;
    }

    // -------- 模拟数据：多层动态波纹效果 --------
    const now = Date.now() / 1000;
    timeOffset += 0.02; // 时间偏移，让波纹动态移动
    
    for (let i = 0; i < bufferLength; i++) {
      const p = i / bufferLength;
      // 组合多个频率的正弦波，创造平滑的波纹
      const v =
        Math.sin(p * Math.PI * 2 + now * 2.2 + timeOffset) * 0.55 +
        Math.sin(p * Math.PI * 4 + now * 1.4 + timeOffset * 0.7) * 0.35 +
        Math.sin(p * Math.PI * 6 + now * 0.8 + timeOffset * 0.5) * 0.15;
      // 映射到 0-255
      dataArray[i] = Math.floor((v + 1) * 128);
    }

    // 清画布
    ctx.clearRect(0, 0, width, height);

    const sliceWidth = width / bufferLength;
    const centerY = height / 2;
    const maxDisplayAmplitude = (height / 2) * 0.65;

    // 创建颜色渐变
    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    if (props.isCanceling) {
      gradient.addColorStop(0, "rgba(255, 103, 103, 1)");
      gradient.addColorStop(1, "rgba(255, 153, 153, 1)");
    } else {
      gradient.addColorStop(0, "rgba(150, 96, 238, 1)"); // 紫色
      gradient.addColorStop(1, "rgba(53, 198, 253, 1)"); // 蓝色
    }

    // 绘制多层波纹（从后往前，后面的更透明）
    const layers = [
      { opacity: 0.6, offset: -0.3, blur: 2, lineWidth: 1.5 }, // 最底层，最透明
      { opacity: 0.8, offset: -0.15, blur: 1.5, lineWidth: 2 }, // 中间层
      { opacity: 1.0, offset: 0, blur: 0, lineWidth: 2.2 } // 主层，最清晰
    ];

    layers.forEach((layer) => {
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.lineWidth = layer.lineWidth;
      ctx.strokeStyle = gradient;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      
      // 添加模糊效果（通过绘制多条重叠的线来模拟）
      // 注意：shadowBlur 会导致模糊，在高分辨率屏幕上减少或移除
      if (layer.blur > 0) {
        const pixelRatio = window.devicePixelRatio || 1;
        // 在高分辨率屏幕上减少模糊效果
        ctx.shadowBlur = layer.blur / pixelRatio;
        ctx.shadowColor = props.isCanceling 
          ? "rgba(255, 103, 103, 0.5)" 
          : "rgba(150, 96, 238, 0.5)";
      }

      ctx.beginPath();

      let prevX = 0;
      let prevY = centerY;

      // 采样间隔稍大一点，避免太锯齿
      for (let i = 0; i < bufferLength; i += 2) {
        const v = dataArray[i] / 128 - 1; // -1~1
        const absV = Math.min(Math.abs(v), 1);
        const enhancedV = absV < 0.25 ? absV * 1.6 : absV;
        const clampedV = v >= 0 ? enhancedV : -enhancedV;

        // 添加层偏移，让每层略有不同
        const layerOffset = layer.offset * maxDisplayAmplitude * 0.3;
        const displayAmplitude = clampedV * maxDisplayAmplitude;
        const y = Math.max(4, Math.min(height - 4, centerY + displayAmplitude + layerOffset));
        const x = i * sliceWidth;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          const midX = (prevX + x) / 2;
          const midY = (prevY + y) / 2;
          ctx.quadraticCurveTo(prevX, prevY, midX, midY);
        }

        prevX = x;
        prevY = y;
      }

      ctx.stroke();
      ctx.restore();
    });
  }

  draw();
}

// 绘制波形（参考 HTML 示例样式，音量控制波动程度）
function drawWaveform(dataArray, frequencyDataArray, bufferLength) {
  if (!waveCanvas.value || !analyser.value) return;

  const canvas = waveCanvas.value;
  const ctx = canvas.getContext("2d", { 
    alpha: true,
    desynchronized: false 
  });
  // 确保渲染质量设置（每次获取 context 时都需要设置）
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  // 使用显示尺寸（ctx 已经 scale 过了，所以使用显示尺寸）
  const width = 253; // 显示宽度
  const height = 100; // 显示高度
  
  // 核心参数（使用 walin.html 的数值）
  const THICK_LINE_HEIGHT_RATIO = 3 / 5; // 粗线高度比例（3/5）
  const DEFAULT_SAMPLING_SIZE = 80; // walin.html 使用 80（更高采样，波形更顺滑）
  const DEFAULT_OFFSET_SPEED = 250.0; // walin.html 的数值
  const DEFAULT_SENSIBILITY = 8; // walin.html 的数值
  const DEFAULT_VOLUME = 80; // walin.html 的初始音量值
  
  // 波浪参数（使用 HTML 示例的数值）
  let samplingSize = DEFAULT_SAMPLING_SIZE;
  let offsetSpeed = DEFAULT_OFFSET_SPEED;
  let sensibility = DEFAULT_SENSIBILITY;
  const BASE_VOLUME = 1; // 基础音量（最小波动值，确保始终有波动）
  let currentVolume = DEFAULT_VOLUME; // 当前音量（0-100），初始值设为默认音量
  let targetVolume = DEFAULT_VOLUME; // 目标音量（初始值）
  let perVolume = sensibility * 0.8; // 音量变化速度（使用 HTML 示例的计算方式）
  
  // 线条宽度（使用 walin.html 的数值）
  const thickLineWidth = 2; // 主线宽度（从5调整为3，更细）
  const fineLineWidth = 1; // walin.html 使用 1
  
  // 渐变颜色配置（使用 walin.html 的渐变色配置）
  // HTML 中取消时使用红底白纹：背景 #E63946，线条 #FFFFFF（纯白色）
  // 注意：getThickColors 和 wavePositions 已在组件顶层定义
  
  const getFineColors = () => {
    if (props.isCanceling) {
      // 取消时使用纯白色（不需要渐变，直接返回白色）
      return [WHITE_WAVE_COLOR];
    }
    // 使用 walin.html 的渐变色（粗细线共用同一渐变）
    return [
      'rgba(136, 128, 255, 0.0)',  // 最左：完全透明
      'rgba(134, 132, 255, 1.0)',  // #8684FF
      'rgba(122, 158, 255, 1.0)',  // #7A9EFF
      'rgba(107, 187, 255, 1.0)',  // #6BBBFF
      'rgba(99, 223, 255, 1.0)',   // #63DFFF
      'rgba(97, 113, 231, 0.0)'    // 最右：完全透明
    ];
  };
  
  // 四条波浪线的函数系数（增大辅助线的波动高度）
  const pathFuncs =  [0.8, 0.35, 0.1, -0.1]; // 粗线 0.8，辅助线大幅提高到 0.7/0.5/0.3，波动更明显
  
  // 采样点和映射
  let samplingX = [];
  let mapX = [];
  let centerHeight = 0;
  let amplitude = 0;
  let recessionFuncs = new Map();
  let totalOffset = 0;
  let lastTimestamp = 0;
  
  // 初始化采样点
  function initSamplingPoints() {
    // 使用显示宽度（不是 canvas 的实际像素宽度）
    const displayWidth = 253;
    samplingX = new Array(samplingSize + 1);
    mapX = new Array(samplingSize + 1);
    const gap = displayWidth / samplingSize;
    
    for (let i = 0; i <= samplingSize; i++) {
      const x = i * gap;
      samplingX[i] = x;
      mapX[i] = (x / displayWidth) * 4 - 2;
    }
  }
  
  // 计算波形函数值（参考 HTML 示例）
  function calcValue(mapX, offset) {
    const keyX = Math.round(mapX * 1000);
    offset = offset % 2;
    const sinFunc = Math.sin(Math.PI * mapX - offset * Math.PI);
    let recessionFunc;
    
    if (recessionFuncs.has(keyX)) {
      recessionFunc = recessionFuncs.get(keyX);
    } else {
      recessionFunc = 4 / (4 + Math.pow(mapX, 4));
      recessionFuncs.set(keyX, recessionFunc);
    }
    
    return sinFunc * recessionFunc;
  }
  
  // 获取粗波浪的淡化渐变（使用 walin.html 的统一渐变函数）
  function getThickWaveGradient() {
    const colors = getThickColors();
    // 取消时使用纯白色，不需要渐变（与 walin.html 一致）
    if (props.isCanceling && colors.length === 1) {
      return colors[0];
    }
    // width 已经是显示宽度（253）
    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    for (let i = 0; i < colors.length; i++) {
      gradient.addColorStop(wavePositions[i], colors[i]);
    }
    return gradient;
  }
  
  // 获取细线的淡化渐变（使用 walin.html 的统一渐变函数）
  function getFineWaveGradient() {
    const colors = getFineColors();
    // 取消时使用纯白色，不需要渐变（与 walin.html 一致）
    if (props.isCanceling && colors.length === 1) {
      return colors[0];
    }
    // width 已经是显示宽度（253）
    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    for (let i = 0; i < colors.length; i++) {
      gradient.addColorStop(wavePositions[i], colors[i]);
    }
    return gradient;
  }
  
  // 平滑改变音量（使用 HTML 示例的逻辑）
  function softerChangeVolume() {
    if (currentVolume < targetVolume - perVolume) {
      currentVolume += perVolume * 2;
    } else if (currentVolume > targetVolume + perVolume) {
      currentVolume -= perVolume * 2;
    } else {
      currentVolume = targetVolume;
    }
  }
  
  // 初始化参数
  function initParameters() {
    // 使用显示尺寸（不是 canvas 的实际像素尺寸）
    const displayWidth = 253;
    const displayHeight = 100;
    
    if (displayWidth === 0 || displayHeight === 0) {
      // 如果尺寸未设置，延迟初始化
      setTimeout(() => {
        if (displayWidth > 0 && displayHeight > 0) {
          initParameters();
        }
      }, 100);
      return;
    }
    
    // 使用显示尺寸进行计算（ctx 已经 scale 过了）
    centerHeight = displayHeight / 2;
    // 粗线振幅计算（使用 walin.html 的数值）
    const originalAmplitude = displayHeight / 2; // walin.html 使用 height / 2
    amplitude = originalAmplitude * THICK_LINE_HEIGHT_RATIO;
    initSamplingPoints();
    perVolume = sensibility * 0.8; // 使用 HTML 示例的计算方式
    totalOffset = 0;
    lastTimestamp = 0;
    currentVolume = DEFAULT_VOLUME; // 初始音量设为默认音量
    targetVolume = DEFAULT_VOLUME; // 初始目标音量设为默认音量
  }
  
  // 初始化（延迟确保 width 和 height 已设置）
  setTimeout(() => {
    initParameters();
  }, 0);
  
  function draw(timestamp) {
    if (!isStart.value || !analyser.value) {
      if (animationFrameId.value) {
        cancelAnimationFrame(animationFrameId.value);
        animationFrameId.value = null;
      }
      lastTimestamp = 0;
      return;
    }

    animationFrameId.value = requestAnimationFrame(draw);

    // 获取时域数据（用于分析音量）
    analyser.value.getByteTimeDomainData(dataArray);
    
    // 计算时间差
    if (!lastTimestamp || lastTimestamp === 0) {
      lastTimestamp = timestamp;
    }
    const deltaTime = timestamp - lastTimestamp;
    lastTimestamp = timestamp;
    
    // 确保 deltaTime 有效
    if (deltaTime <= 0 || deltaTime > 1000) {
      return;
    }

    // 清空画布（透明背景，只保留波纹，背景色由 CSS 控制）
    ctx.clearRect(0, 0, width, height);
    
    if (width === 0 || height === 0) return;
    
    // 累计偏移量（确保动画持续进行）
    totalOffset += deltaTime / offsetSpeed;
    // 限制偏移量范围，避免数值过大
    if (totalOffset > 1000) {
      totalOffset = totalOffset % 2;
    }
    const currentOffset = totalOffset;
    
    // 分析音量（振幅）：计算时域数据的音量大小（提高灵敏度）
    let volumeSum = 0;
    let maxVolume = 0;
    for (let i = 0; i < bufferLength; i++) {
      const sample = dataArray[i];
      const volume = Math.abs(sample - 128); // 距离中心点的距离
      volumeSum += volume;
      if (volume > maxVolume) {
        maxVolume = volume;
      }
    }
    
    // 提高灵敏度：使用最大值和平均值的加权组合
    // 最大值权重更高，能更快响应音量变化
    const avgVolume = volumeSum / bufferLength;
    const combinedVolume = maxVolume * 0.6 + avgVolume * 0.4; // 最大值占60%，平均值占40%
    
    // 转换为 0-100 范围，并增加灵敏度倍数
    const sensitivityMultiplier = 2.0; // 提高灵敏度倍数，让音量响应更明显
    const calculatedVolume = (combinedVolume / 128) * 100 * sensitivityMultiplier;
    
    // 音量范围：BASE_VOLUME（最小，30）到 100（最大）
    // calculatedVolume 是 0-100 范围的值，映射到 BASE_VOLUME 到 100 之间
    const volumeRange = 100 - BASE_VOLUME; // 可用音量范围（70）
    // 将 calculatedVolume（0-100）映射到 BASE_VOLUME 到 100 之间
    targetVolume = Math.min(100, BASE_VOLUME + (calculatedVolume>3?calculatedVolume+50:calculatedVolume) * (volumeRange / 100));
    
    // 平滑过渡音量
    softerChangeVolume();

    // 1. 绘制细线（带两头淡化）
    for (let n = 1; n < 4; n++) {
      ctx.beginPath();
      ctx.moveTo(0, centerHeight);
      
      for (let i = 0; i <= samplingSize; i++) {
        const x = samplingX[i];
        const curY = amplitude * calcValue(mapX[i], currentOffset);
        const realY = curY * pathFuncs[n] * (currentVolume / 100);
        ctx.lineTo(x, centerHeight + realY);
      }
      
      // 细线样式：带淡化渐变（使用 walin.html 的透明度）
      ctx.lineWidth = fineLineWidth;
      ctx.strokeStyle = getFineWaveGradient();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      // 取消时完全不透明（1），正常时 0.55（与 walin.html 一致）
      ctx.globalAlpha = props.isCanceling ? 1 : 0.55;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // 2. 绘制粗波浪（3/5高度+淡化）
    ctx.beginPath();
    ctx.moveTo(0, centerHeight);
    
    for (let i = 0; i <= samplingSize; i++) {
      const x = samplingX[i];
      const curY = amplitude * calcValue(mapX[i], currentOffset);
      const realY = curY * pathFuncs[0] * (currentVolume / 100);
      ctx.lineTo(x, centerHeight + realY);
    }
    
    // 粗线样式
    ctx.lineWidth = thickLineWidth;
    ctx.strokeStyle = getThickWaveGradient();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  }

  draw();
}

// 停止可视化
function stopVisualization() {
  if (animationFrameId.value) {
    cancelAnimationFrame(animationFrameId.value);
    animationFrameId.value = null;
  }

  if (audioContext.value) {
    audioContext.value.close();
    audioContext.value = null;
  }

  analyser.value = null;
}

// 重置所有状态
function resetAllStates() {
  isStart.value = false;
  shouldSaveRecording = true; // 重置保存标记
  stopRecording();
  stopVisualization();
}

// 处理关闭
function handleClose() {
  stopRecording();
  localShowPopUp.value = false;
  emit("update:showPopUp", false);
  emit("close");
}

onMounted(() => {
  // 组件挂载时的初始化
});

onUnmounted(() => {
  stopRecording();
  stopVisualization();
});
</script>

<style scoped lang="scss">
.voice-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.voice-popup-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
}

.no-audio-tip-container {
  margin-top: 150px;
  width: 100%;
}

.recwave {
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 281px;
  height: 102px;
  box-shadow: inset 0px 1px 16px 0px rgba(255, 255, 255, 0.3);
  border-radius: 23px;
  position: relative;
  overflow: hidden;

  .recwave-WaveView {
    width: 253px; // 90%容器宽度，与静态线条长度一致
    height: 100px;
    display: block;
  }

  .line {
    width: 90%;
    margin: 0 auto;
    height: 2px;
    /* 渐变背景通过内联样式动态设置 */
  }
}

.cancel-btn {
  margin-top: 41px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;

  .cancel-icon {
    width: 37px;
    height: 37px;
    margin-bottom: 15px;
  }
}

.voice-btn {
  position: absolute;
  top: 440px;
  background: url("@/assets/voice-bottom.png") no-repeat center center;
  background-size: cover;
  overflow: hidden;
  width: 100%;
  height: 160px;
  text-align: center;
  display: flex;
  justify-content: center;

  .voice-icon-img {
    width: 29px;
    height: 40px;
    margin-top: 60px;
  }
}
</style>
